import type { Frequency, IndicatorSeries, Observation, RangeKey, Unit, ViewMode } from "../types";

const RANGE_YEARS: Record<Exclude<RangeKey, "MAX">, number> = {
  "1Y": 1,
  "3Y": 3,
  "5Y": 5,
  "10Y": 10
};

function parseDate(date: string) {
  return new Date(`${date}T00:00:00Z`).getTime();
}

function subtractCalendarYears(date: string, years: number) {
  const m = date.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return Number.NaN;
  const y = Number(m[1]) - years;
  const month = Number(m[2]) - 1;
  const day = Number(m[3]);
  // Clamp Feb-29 to the final valid day of February in a non-leap target year.
  const maxDay = new Date(Date.UTC(y, month + 1, 0)).getUTCDate();
  return Date.UTC(y, month, Math.min(day, maxDay));
}

export function filterByRange(series: IndicatorSeries, range: RangeKey): IndicatorSeries {
  if (range === "MAX" || series.observations.length === 0) return series;
  const last = [...series.observations].reverse().find((o) => o.value != null) ?? series.observations.at(-1);
  if (!last) return series;
  const cutoff = subtractCalendarYears(last.date, RANGE_YEARS[range]);
  if (!Number.isFinite(cutoff)) return series;
  return {
    ...series,
    observations: series.observations.filter((o) => parseDate(o.date) >= cutoff)
  };
}

function observedPeriodsFor(mode: ViewMode, frequency: Frequency): number | null {
  if (frequency !== "daily" && frequency !== "weekly") return null;
  if (mode === "change_1m") return frequency === "daily" ? 21 : 4;
  if (mode === "change_3m") return frequency === "daily" ? 63 : 13;
  if (mode === "yoy") return frequency === "daily" ? 252 : 52;
  return null;
}

function calendarLagMonths(mode: ViewMode, frequency: Frequency): number | null {
  if (frequency === "monthly") {
    if (mode === "change_1m") return 1;
    if (mode === "change_3m") return 3;
    if (mode === "yoy") return 12;
  }
  if (frequency === "quarterly") {
    if (mode === "change_1m") return null;
    if (mode === "change_3m") return 3;
    if (mode === "yoy") return 12;
  }
  if (frequency === "annual") {
    if (mode === "change_1m" || mode === "change_3m") return null;
    if (mode === "yoy") return 12;
  }
  return null;
}

function shiftMonthDate(date: string, deltaMonths: number) {
  const m = date.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return null;
  const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1 + deltaMonths, 1));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}-01`;
}

function differenceUnit(unit: Unit): Unit {
  if (unit === "%") return "pp";
  return unit;
}


export function transformSeries(series: IndicatorSeries, mode: ViewMode): IndicatorSeries {
  if (mode === "level") return series;

  if (mode === "zscore") {
    let n = 0;
    let mean = 0;
    let m2 = 0;
    const observations = series.observations.map((o) => {
      if (o.value == null) return { date: o.date, value: null };
      n += 1;
      const delta = o.value - mean;
      mean += delta / n;
      const delta2 = o.value - mean;
      m2 += delta * delta2;
      const variance = n > 1 ? m2 / n : 0;
      const sd = Math.sqrt(variance);
      return { date: o.date, value: n < 2 || sd === 0 ? null : Number(((o.value - mean) / sd).toFixed(4)) };
    });
    return {
      ...series,
      id: `${series.id}__zscore`,
      shortName: `${series.shortName} Z`,
      name: `${series.name} expanding Z-score`,
      unit: "score",
      source: {
        ...series.source,
        note: [series.source.note, "Z-score is expanding-window (data available up to each observation only); no future look-ahead."].filter(Boolean).join(" "),
        sourceIds: [series.id]
      },
      observations
    };
  }

  if (mode === "percentile") {
    const sorted: number[] = [];
    const observations = series.observations.map((o) => {
      if (o.value == null) return { date: o.date, value: null };
      let lo = 0;
      let hi = sorted.length;
      while (lo < hi) {
        const mid = Math.floor((lo + hi) / 2);
        if (sorted[mid] <= o.value) lo = mid + 1;
        else hi = mid;
      }
      sorted.splice(lo, 0, o.value);
      let upper = lo;
      while (upper + 1 < sorted.length && sorted[upper + 1] <= o.value) upper += 1;
      const pct = ((upper + 1) / sorted.length) * 100;
      return { date: o.date, value: Number(pct.toFixed(2)) };
    });
    return {
      ...series,
      id: `${series.id}__pct`,
      shortName: `${series.shortName} pct`,
      name: `${series.name} expanding historical percentile`,
      unit: "percentile",
      source: {
        ...series.source,
        note: [series.source.note, "Percentile is expanding-window through each observation; future observations are excluded."].filter(Boolean).join(" "),
        sourceIds: [series.id]
      },
      observations
    };
  }

  const frequency = series.source.frequency;
  const observedLag = observedPeriodsFor(mode, frequency);
  const calendarLag = calendarLagMonths(mode, frequency);
  const byDate = new Map(series.observations.map((o) => [o.date, o.value]));
  const unsupported = (frequency === "quarterly" || frequency === "annual") && calendarLag == null;
  const observations: Observation[] = series.observations.map((o, i) => {
    if (o.value == null || unsupported) return { date: o.date, value: null };
    let previous: number | null = null;
    if (observedLag != null) {
      const prev = series.observations[i - observedLag];
      previous = prev?.value ?? null;
    } else if (calendarLag != null) {
      const target = shiftMonthDate(o.date, -calendarLag);
      previous = target ? (byDate.get(target) ?? null) : null;
    }
    if (previous == null) return { date: o.date, value: null };
    return { date: o.date, value: Number((o.value - previous).toFixed(4)) };
  });

  const approximation = frequency === "daily"
    ? "Daily change windows use trading-observation approximations: 21/63/252 observations for 1M/3M/YoY; missing observations are not filled."
    : frequency === "weekly"
      ? "Weekly change windows use observed-week approximations: 4/13/52 observations; missing observations are not filled."
      : unsupported
        ? `${mode} is not defined at ${frequency} frequency; values remain N/A.`
        : "Calendar-frequency changes require the exact prior calendar period; missing months/quarters/years remain N/A.";
  return {
    ...series,
    id: `${series.id}__${mode}`,
    shortName: `${series.shortName} Δ`,
    name: `${series.name} ${mode}`,
    unit: differenceUnit(series.unit),
    source: {
      ...series.source,
      note: [series.source.note, approximation].filter(Boolean).join(" "),
      sourceIds: [series.id]
    },
    observations
  };
}

export function mergeSeries(series: IndicatorSeries[]) {
  const dates = Array.from(new Set(series.flatMap((s) => s.observations.map((o) => o.date)))).sort();
  const maps = series.map((s) => new Map(s.observations.map((o) => [o.date, o.value])));
  return dates.map((date) => {
    const row: Record<string, string | number | null> = { date };
    series.forEach((s, i) => {
      row[s.id] = maps[i].get(date) ?? null;
    });
    return row;
  });
}

export function latestValue(series: IndicatorSeries) {
  return [...series.observations].reverse().find((o) => o.value != null)?.value ?? null;
}

export function latestChange(series: IndicatorSeries) {
  const values = series.observations.filter((o) => o.value != null);
  if (values.length < 2) return null;
  return (values.at(-1)!.value as number) - (values.at(-2)!.value as number);
}

export function summarize(series: IndicatorSeries) {
  const values = series.observations.flatMap((o) => (o.value == null ? [] : [o.value]));
  if (!values.length) return { latest: null, min: null, max: null, mean: null };
  return {
    latest: latestValue(series),
    min: Math.min(...values),
    max: Math.max(...values),
    mean: values.reduce((a, b) => a + b, 0) / values.length
  };
}

export function unitLabel(unit: Unit) {
  switch (unit) {
    case "%": return "%";
    case "pp": return "%p";
    case "bp": return "bp";
    case "trillion_krw": return "조원";
    case "eok_krw": return "억원";
    case "score": return "σ";
    case "percentile": return "%ile";
    case "index": return "지수";
    case "count": return "건";
    case "housing_units": return "호";
    case "krw_per_usd": return "원/$";
  }
}
